export type Language = 'en' | 'es';

export const translations = {
  en: {
    // Common
    next: 'Next',
    back: 'Back',
    complete: 'Complete Setup',
    loading: 'Loading…',
    selectOption: 'Select an option',
    required: 'This field is required',

    // Language step
    onboardingTitle: 'Welcome to VizitPro',
    onboardingSubtitle: "Let's get your business set up in a few quick steps.",
    preferredLanguage: 'Preferred Language',
    english: 'English',
    spanish: 'Spanish',
    businessName: 'Business Name',
    businessNamePlaceholder: 'e.g. Sunrise Cleaning Co.',
    businessType: 'Business Type',
    businessTypes: {
      cleaning: 'Cleaning Services',
      landscaping: 'Landscaping Services',
    },

    // Profile step
    profileTitle: 'Your Information',
    profileSubtitle: 'Tell us about the person running this business.',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Jane Doe',
    address: 'Business Address',
    addressPlaceholder: '123 Main St, City, State, ZIP',
    phone: 'Phone Number',
    phonePlaceholder: '(555) 000-0000',

    // Tier step
    tierTitle: 'Choose Your Plan',
    tierSubtitle: 'Pick the plan that fits your team. You can change anytime.',
    perMonth: '/mo',
    users: 'Users',
    customers: 'Customers',
    unlimited: 'Unlimited',
    upTo: 'Up to',
    features: 'Features',
    scheduling: 'Scheduling',
    invoices: 'Invoices',
    quotes: 'Quotes',
    messaging: 'Messaging',
    businessPage: 'Business Page',
    payments: 'Payments',
    analytics: 'Analytics',
    paymentFees: 'Payment Fees',
    selectPlan: 'Select Plan',
    recommended: 'Recommended',
    tiers: {
      solo: {
        name: 'Solo',
        price: '$34.99',
        users: '1',
        businessPage: 'Basic',
        paymentFees: 'Pass-through',
        analytics: 'Basic',
      },
      team: {
        name: 'Team',
        price: '$49',
        users: 'Up to 5',
        businessPage: 'Full',
        paymentFees: '2.2% + $0.30',
        analytics: 'Standard',
      },
      pro: {
        name: 'Pro',
        price: '$99',
        users: 'Up to 20',
        businessPage: 'Full + Custom Domain',
        paymentFees: 'Free',
        analytics: 'Advanced',
      },
    },

    // Navigation
    jobs: 'Jobs',
    quotesNav: 'Quotes',
    invoicesNav: 'Invoices',
    customersNav: 'Customers',
    businessPageNav: 'Business Page',
    employees: 'Employees',
    paymentsNav: 'Payments',
    signOut: 'Sign out',

    // Dashboard placeholder
    comingSoon: 'Coming soon',
    pageUnderConstruction: 'This section is under construction.',

    // Step labels
    stepBusiness: 'Business',
    stepProfile: 'Profile',
    stepPlan: 'Plan',

    // Payments page
    paymentMethodsTitle: 'Payment Methods',
    paymentMethodsSubtitle: 'Configure how your customers can pay you.',
    addPaymentMethod: 'Add Payment Method',
    configured: 'Configured',
    notConfigured: 'Not configured',
    configure: 'Configure',
    editConfig: 'Edit',
    removeConfig: 'Deactivate',
    saveConfig: 'Save',
    cancel: 'Cancel',
    chooseMethodToAdd: 'Choose a payment method to add',
    paymentMethodLabelField: 'Label',
    paymentMethodLabelPlaceholder: 'e.g. Main account',
    paymentMethodActive: 'Active',
    paymentMethodInactive: 'Inactive',
    paymentMethodCreated: 'Payment method added',
    paymentMethodUpdated: 'Payment method updated',

    // Zelle
    zelleTitle: 'Zelle',
    zelleDescription: 'Accept payments via Zelle. Share your phone number or email.',
    zellePhone: 'Phone Number or Email',
    zellePhonePlaceholder: 'e.g. (555) 000-0000 or you@example.com',

    // Stripe
    stripeTitle: 'Stripe',
    stripeDescription: 'Accept credit/debit cards securely via Stripe.',
    stripeConnectTitle: 'Accept card payments',
    stripeConnectDescription: 'Connect with Stripe so customers can pay invoices by card.',
    stripeConnectConnected: 'Connected',
    stripeConnectNotConnected: 'Not connected',
    stripeConnectButton: 'Connect with Stripe',
    stripeConnectPending: 'Onboarding started — finish it on Stripe to start accepting cards.',
    stripeConnectError: 'Could not start Stripe onboarding.',

    // Venmo
    venmoTitle: 'Venmo',
    venmoDescription: 'Accept payments via Venmo.',
    venmoUsername: 'Venmo Username',
    venmoUsernamePlaceholder: '@YourBusiness',

    // Cash & check
    cashTitle: 'Cash',
    cashDescription: 'Accept in-person cash payments.',
    checkTitle: 'Check',
    checkDescription: 'Accept paper checks.',

    // Jobs page
    todaysJobs: "Today's Jobs",
    allJobs: 'All Jobs',
    filterByCustomer: 'Filter by customer',
    filterByDate: 'Date',
    filterByType: 'Type',
    allTypes: 'All Types',
    recurring: 'Recurring',
    oneTime: 'One-time',
    allDates: 'All Dates',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    jobType: 'Type',
    jobStatus: 'Status',
    scheduled: 'Scheduled',
    inProgress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    customer: 'Customer',
    assignedTo: 'Assigned to',
    jobAddress: 'Address',
    duration: 'Duration',
    noJobsFound: 'No jobs found',
    noJobsDescription: 'Try adjusting your filters or add a new job.',
    addJob: 'Add Job',
    previous: 'Previous',
    nextPage: 'Next',
    pageOf: 'of',
    showingJobs: 'Showing',
    jobsCount: 'jobs',
    clearFilters: 'Clear filters',
    searchCustomer: 'Search customer…',

    // Employee schedule
    schedule: 'Schedule',
    scheduleVisitsCount: 'visits',
    scheduleEmpty: 'No visits scheduled',
    employeeScheduleBack: 'Back to schedule',

    // Calendar view
    calendarView: 'Calendar',
    listView: 'List',
    tableView: 'Table',
    calendarPrevMonth: 'Previous month',
    calendarNextMonth: 'Next month',
    calendarMore: '+{n} more',
    calendarNoJobsMonth: 'No jobs scheduled this month',
    calendarNoVisitsMonth: 'No visits scheduled this month',
    calendarViewMonth: 'Month',
    calendarViewAgenda: 'Agenda',
    calendarAgendaDate: 'Date',
    calendarAgendaTime: 'Time',
    calendarAgendaEvent: 'Event',
    calendarAllDay: 'All day',
    calendarNoEventsRange: 'Nothing scheduled in this range',
    jobRescheduled: 'Job rescheduled',
    visitRescheduled: 'Visit rescheduled',
    reschedule: 'Reschedule',
    rescheduleCurrentLabel: 'Currently scheduled',
    rescheduleNewLabel: 'New date',

    // Add job modal
    addJobTitle: 'Add New Job',
    copyJobTitle: 'Copy Job',
    jobCopyAction: 'Copy',
    jobCopyJob: 'Copy Job',
    jobTitle: 'Job Title',
    jobTitlePlaceholder: 'e.g. Weekly House Cleaning',
    jobCustomer: 'Customer',
    jobCustomerPlaceholder: 'Select a customer',
    jobCustomerNew: 'New customer…',
    jobNoEligibleCustomers: 'No customers with an account yet.',
    jobManageCustomers: 'Manage customers',
    jobCustomerNoAccount:
      'That customer has no linked account yet, so a job cannot be created for them.',
    jobAssignedTo: 'Assigned To',
    jobAssignedToPlaceholder: 'Select employee',
    jobDate: 'Date',
    jobStartDate: 'Start date',
    jobEndDate: 'End date',
    jobEndDateModeSame: 'Same day',
    jobEndDateModeCustom: 'Custom',
    jobEndDateModeYear: 'Year contract',
    jobStartTime: 'Start time',
    jobEndTime: 'End time',
    jobSelectTimePlaceholder: 'Select time',
    jobTime: 'Time',
    jobDuration: 'Duration',
    jobDurationPlaceholder: 'e.g. 2 hrs',
    jobDescription: 'Description',
    jobDescriptionPlaceholder: 'What needs to get done…',
    jobTotalAmount: 'Total amount',
    jobTotalAmountPlaceholder: '0.00',
    jobNotes: 'Notes (optional)',
    jobNotesPlaceholder: 'Any special instructions…',
    jobTypeLabel: 'Job Type',
    saveJob: 'Save Job',
    jobAdded: 'Job added',
    newCustomerName: 'New Customer Name',
    newCustomerNamePlaceholder: 'Full name or business name',

    // Job recurrence
    jobRepeats: 'Repeats',
    jobFrequency: 'Frequency',
    frequencyWeekly: 'Weekly',
    frequencyMonthly: 'Monthly',
    frequencyCustom: 'Custom',
    jobRepeatEvery: 'Repeat every',
    jobRepeatEveryWeeks: 'week(s)',
    jobRepeatEveryMonths: 'month(s)',
    jobDaysOfWeek: 'On days',
    jobDayOfMonth: 'Day of month',
    jobCustomRule: 'Custom rule (RRULE)',
    jobCustomRulePlaceholder: 'FREQ=DAILY;INTERVAL=2',
    jobSeriesEndDate: 'Ends on',
    weekdayShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],

    // Job team / employee assignment
    jobTeam: 'Assigned team',
    jobAssignEmployee: 'Assign employee',
    jobSelectEmployee: 'Select an employee',
    jobPayAmount: 'Pay',
    jobPayAmountPlaceholder: '0.00',
    jobAssignAdd: 'Assign',
    jobUnassign: 'Remove',
    jobUnassignConfirm: 'Confirm remove?',
    jobNoAssignedEmployees: 'No one assigned yet.',
    jobAllEmployeesAssigned: 'All active employees are assigned.',
    jobEmployeeAssigned: 'Employee assigned',
    jobEmployeeRemoved: 'Employee removed',
    jobEmployeePayUpdated: 'Pay updated',
    jobBadgeUnassigned: 'Unassigned',
    jobBadgeAssigned: 'assigned',
    jobBadgeVisits: 'visits',
    assignmentAll: 'All assignees',
    assignmentUnassigned: 'Unassigned',
    assignmentAssigned: 'Assigned',

    // Job detail modal
    jobUpdated: 'Job updated',
    editJob: 'Edit job',
    paymentStatusLabel: 'Payment Status',
    paymentUnpaid: 'Unpaid',
    paymentPaid: 'Paid',

    // Recurrence preview (add-job form)
    recurrencePreviewCreatesOne: 'Creates 1 visit:',
    recurrencePreviewCreates: 'Creates {n} visits:',
    recurrencePreviewFirstN: 'first {n} shown',
    recurrencePreviewPickDays: 'Pick at least one weekday to preview dates.',
    recurrencePreviewCustom: 'Preview not available for custom rules.',

    // Recurring job series
    seriesTitle: 'Recurring series',
    seriesBack: 'Back to jobs',
    seriesOwnerOnly: 'Only owners can view a series.',
    seriesUpsellTitle: 'Recurring jobs is a paid feature',
    seriesUpsellBody: 'Upgrade to the Team or Pro plan to schedule recurring visits.',
    seriesViewSeries: 'View series',
    seriesLoadError: 'Could not load this series.',
    seriesPipelineSummary:
      '{total} visits · {completed} done · {cancelled} cancelled',
    seriesPipelineNext: 'next {date}',
    seriesPipelineNoNext: 'no upcoming visits',
    seriesLaborPerVisit: 'Labor / visit',
    seriesExpectedPerVisit: 'Expected / visit',
    seriesProfitPerVisit: 'Profit / visit',
    seriesVisitsCompleted: 'Visits',
    seriesLaborVisits: '{n} visits',
    seriesLastServiced: 'Last serviced {rel}',
    seriesNeverServiced: 'No visits completed yet',
    seriesNextVisit: 'Next visit {rel}',

    // Series crew bar
    seriesCrew: 'Crew',
    seriesAddCrew: 'Add crew to series',
    seriesCrewEmployee: 'Employee',
    seriesCrewSelectEmployee: 'Select an employee',
    seriesScopeLabel: 'Applies to',
    seriesScopeAll: 'Every visit in the series',
    seriesScopeUpcoming: 'Upcoming visits only',
    seriesScopeUnstarted: 'Visits not yet started',
    seriesChangePay: 'Change pay across series',
    seriesApplyPay: 'Apply',
    seriesRemoveFromSeries: 'Remove from series',
    seriesRemoveConfirm: 'Confirm remove',
    seriesRemoveHint:
      'Removes from upcoming visits; completed visits keep their pay record.',
    seriesCrewNoAssignments: 'No crew assigned across this series yet.',
    seriesCrewNoMatch: 'No visits matched that scope.',
    seriesCrewAlreadyAll: 'Already assigned to all matching visits.',
    seriesPayNoChange: 'No pay changes needed.',
    seriesCrewAddedTo: 'Added {name} to {n} visits.',
    seriesPayUpdatedOn: 'Updated pay on {n} visits.',
    seriesCrewRemovedFrom: 'Removed {name} from {n} visits.',
    seriesCrewCancel: 'Cancel',

    // Occurrence table + filters
    seriesColDate: 'Date',
    seriesColStatus: 'Status',
    seriesColPayment: 'Payment',
    seriesColCrew: 'Crew',
    seriesColBilled: 'Billed',
    seriesColCollected: 'Collected',
    seriesColActions: 'Actions',
    seriesNoOccurrences: 'No visits match these filters.',
    seriesMarkPaid: 'Mark paid',
    seriesSendInvoice: 'Send invoice',
    seriesViewInvoice: 'View invoice',
    seriesSkipVisit: 'Skip this visit',
    seriesSkipConfirm: 'Confirm skip',
    seriesAdvanceToStarted: 'Start',
    seriesAdvanceToCompleted: 'Complete',
    seriesFilterPayment: 'Payment',
    seriesFilterPaymentAll: 'All payments',
    seriesFilterPaymentPaid: 'Paid',
    seriesFilterPaymentUnpaid: 'Unpaid',
    seriesFilterPaymentPartial: 'Partial',
    seriesFilterDate: 'Date',
    seriesFilterDateAll: 'All dates',
    seriesFilterDatePast: 'Past',
    seriesFilterDateUpcoming: 'Upcoming',
    paymentPartial: 'Partial',
    seriesPartialShort: 'Partial — {amount} short',

    // Record payment form
    jobRecordPayment: 'Record payment',
    jobAmountCollected: 'Amount collected',
    jobPaymentMethod: 'Payment method',
    jobDefaultPaymentMethod: 'Default payment method',
    jobDefaultPaymentMethodNone: 'No default',
    paymentMethodZelle: 'Zelle',
    paymentMethodVenmo: 'Venmo',
    paymentMethodStripe: 'Stripe',
    paymentMethodCash: 'Cash',
    paymentMethodCheck: 'Check',
    paymentMethodOther: 'Other',
    jobPaymentRecorded: 'Payment recorded',
    jobPaidOn: 'Paid {date}',
    jobCompletedOn: 'Completed {date}',
    jobPaymentSave: 'Save payment',

    // Job notes
    jobNotesHeading: 'Notes',
    jobNoteBody: 'Note',
    jobNotePlaceholder: 'Add a note about this visit…',
    jobAddNote: 'Add note',
    jobNoteAdded: 'Note added',
    jobNoNotes: 'No notes yet.',

    // Job attachments
    jobAttachmentsHeading: 'Photos & files',
    jobAddAttachment: 'Upload',
    jobAttachmentCaption: 'Caption (optional)',
    jobAttachmentCaptionPlaceholder: 'e.g. Before — front yard',
    jobAttachmentAdded: 'Attachment added',
    jobNoAttachments: 'No photos or files yet.',
    jobAttachmentUploading: 'Uploading…',
    jobAttachmentOpen: 'Open file',

    // Employees page
    addEmployee: 'Add Employee',
    addEmployeeTitle: 'Add Employee',
    allEmployees: 'All Employees',
    activeEmployees: 'Active',
    inactiveEmployees: 'Inactive',
    employeeFullName: 'Full Name',
    employeeFullNamePlaceholder: 'Jane Doe',
    employeePhone: 'Phone',
    employeeEmail: 'Email',
    employeeEmailPlaceholder: 'jane@example.com',
    employeeTitle: 'Title',
    employeeTitlePlaceholder: 'e.g. Lead Cleaner',
    employeeHiredAt: 'Hire Date',
    employeeStatus: 'Status',
    employeeActive: 'Active',
    employeeInactive: 'Inactive',
    employeeNoTitle: 'No title',
    employeeLoadError: 'Could not load this employee.',
    employeeAdded: 'Employee added',
    employeeUpdated: 'Employee updated',
    employeeInviteLink: 'Send invite link',
    employeeInviteLinkReady: 'Invite link ready',
    employeeInviteLinkPanelTitle: 'Employee invite link',
    employeeInviteLinkHint:
      'Share this link so the employee can sign in and set up their account.',
    employeeInviteLinkCopied: 'Link copied',
    employeeInviteLinkCopyFailed: 'Could not copy the link',
    employeeInviteCopy: 'Copy link',
    employeeInviteNative: 'Share',
    employeeInviteEmail: 'Email',
    employeeInviteClose: 'Close',
    employeeInviteEmailSubject: 'Set up your account',
    employeeInviteEmailBody: 'Use the link below to sign in and set up your account:',
    employeeInviteExpiryNote: 'This link is sensitive — do not share it publicly.',
    employeeNoEmailTooltip: 'Add an email address before sending an invite link.',
    saveEmployee: 'Save Employee',
    noEmployeesFound: 'No employees found',
    noEmployeesDescription: 'Try changing the filter or add a new employee.',
    filterByStatus: 'Status',
    allStatuses: 'All',

    // Payroll page
    payrollNav: 'Payroll',
    payrollTitle: 'Payroll',
    payrollEmpty: 'No one is owed anything for this period.',
    payrollFilterFrom: 'From',
    payrollFilterTo: 'To',
    payrollVisitsOwed: 'Visits owed',
    payrollTotalOwed: 'Total owed',
    payrollViewHistory: 'Payment history',
    payrollViewOwed: 'Owed',
    payrollHistoryEmpty: 'No payments recorded yet.',
    payrollBackToList: 'Back to payroll',
    payrollSelectAll: 'Select all',
    payrollDeselectAll: 'Deselect all',
    payrollSelectedTotal: '{count} visits selected · {amount}',
    payrollNotePlaceholder: 'Note (optional), e.g. Zelle, check #123…',
    payrollMarkAsPaid: 'Mark as paid',
    payrollPaymentRecorded: 'Payment recorded',
    payrollPaymentDetailTitle: 'Payment detail',
    payrollNoOwedVisits: 'This employee has nothing outstanding for this period.',
    payrollPaidAt: 'Paid',
    payrollLoadError: 'Could not load payroll data.',
    payrollLineItem: 'Visit',

    // Customers page
    addCustomer: 'Add Customer',
    addCustomerTitle: 'Add Customer',
    searchCustomers: 'Search customers…',
    customerFullName: 'Full Name',
    customerFullNamePlaceholder: 'Jane Doe',
    customerEmail: 'Email',
    customerEmailPlaceholder: 'jane@example.com',
    customerServiceAddress: 'Service Address',
    customerServiceAddressPlaceholder: '1847 Maple Ave, Austin, TX 78701',
    customerNotes: 'Notes',
    customerNotesPlaceholder: 'Any notes about this customer…',
    customerStatus: 'Status',
    saveCustomer: 'Save Customer',
    customerAdded: 'Customer added',
    customerUpdated: 'Customer updated',
    customerLoadError: 'Could not load this customer.',
    noCustomersFound: 'No customers found',
    noCustomersDescription: 'Try a different search or add a new customer.',
    customerActive: 'Active',
    customerInactive: 'Inactive',

    // Quotes page
    quotesTitle: 'Quotes',
    quotesCount: 'quotes',
    addQuote: 'Add Quote',
    addQuoteTitle: 'New Quote',
    quoteJob: 'Job',
    quoteJobPlaceholder: 'Select a job',
    quoteNoJobs: 'Create a job first, then quote it.',
    quoteAmount: 'Amount',
    quoteAmountPlaceholder: '0.00',
    quoteAmountInvalid: 'Enter a valid amount',
    quoteStatus: 'Status',
    quoteStatusPending: 'Pending',
    quoteStatusAccepted: 'Accepted',
    quoteStatusDeclined: 'Declined',
    quotePhaseDraft: 'Draft',
    quotePhaseSent: 'Sent',
    quotePhaseApproved: 'Approved',
    quoteSend: 'Send',
    quoteApprove: 'Approve',
    quoteAccept: 'Accept',
    quoteDecline: 'Decline',
    quoteDeclineConfirm: 'Confirm decline',
    saveQuote: 'Save',
    quoteAdd: 'Add',
    quoteCreated: 'Quote created',
    quoteUpdated: 'Quote updated',
    quoteSentMsg: 'Quote sent',
    quoteApprovedMsg: 'Quote approved',
    quoteRespondedMsg: 'Response saved',
    quoteLoadError: 'Could not load this quote.',
    noQuotesFound: 'No quotes yet',
    noQuotesDescription: 'Create a quote from a job.',
    quotesOwnerOnly: 'Only owners can view all quotes.',
    quoteCreatedAt: 'Created',
    quoteSentAt: 'Sent',
    quoteApprovedAt: 'Approved',
    quoteRespondedAt: 'Responded',
    quoteLineItems: 'Line items',
    quoteLineItemDescription: 'Description',
    quoteLineItemDescriptionPlaceholder: 'e.g. Deep clean, 3 bedrooms',
    quoteLineItemAmount: 'Amount',
    quoteAddLineItem: 'Add line item',
    quoteRemoveLineItem: 'Remove line item',
    quoteNoLineItems: 'No line items',
    quoteLineItemsSubtotal: 'Subtotal',
    quoteNotes: 'Notes',
    quoteNotesPlaceholder: 'Scope of work, terms, exclusions…',
    quoteSetTotalManually: 'Set total manually',
    quoteLineItemsReconcile: 'Line items',
    quoteTotal: 'Total',
    quoteEdit: 'Edit',
    quoteEditDone: 'Done',

    // Quote share links
    quoteShareLink: 'Share link',
    quoteShareLinkReady: 'Share link ready',
    quoteShareLinkPanelTitle: 'Send this quote',
    quoteShareLinkHint:
      'Anyone with this link can view the quote — no login needed.',
    quoteLinkCopied: 'Link copied',
    quoteLinkCopyFailed: 'Could not copy the link',
    quoteShareCopy: 'Copy link',
    quoteShareNative: 'Share',
    quoteShareEmail: 'Email',
    quoteShareText: 'Text',
    quoteShareExpires: 'Expires',
    quoteShareEmailSubject: 'Your quote',
    quoteShareEmailBody: 'Here is your quote. You can view it at the link below:',
    quoteShareClose: 'Close',

    // Public shared-quote page (/q/:token)
    sharedQuoteFor: 'Quote for',
    sharedQuoteSent: 'Sent',
    sharedQuoteExpires: 'Expires',
    sharedQuoteInvalid:
      'This link is no longer valid. It may have expired or been replaced — contact the business for a new one.',
    sharedQuoteRateLimited: 'Too many requests — try again in a moment.',
    sharedQuoteError: 'Something went wrong loading this quote.',
    sharedQuoteRetry: 'Try again',
    sharedQuoteResponderName: 'Your name (optional)',
    sharedQuoteAccept: 'Accept quote',
    sharedQuoteDecline: 'Decline',
    sharedQuoteDeclineConfirm: 'Tap again to confirm',
    sharedQuoteAcceptedMsg: 'You accepted this quote.',
    sharedQuoteDeclinedMsg: 'You declined this quote.',
    sharedQuoteAlreadyResponded: 'This quote has already been responded to.',
    sharedQuoteRespondError: 'Could not save your response. Please try again.',

    // Invoices page
    invoicesTitle: 'Invoices',
    invoicesCount: 'invoices',
    addInvoice: 'Add Invoice',
    addInvoiceTitle: 'New Invoice',
    invoiceJob: 'Job',
    invoiceJobPlaceholder: 'Select a job',
    invoiceNoJobs: 'Create a job first, then invoice it.',
    invoiceSubtotal: 'Subtotal',
    invoiceTax: 'Tax',
    invoiceDiscount: 'Discount',
    invoiceTotal: 'Total',
    invoiceShowTaxBreakdown: 'Show tax breakdown',
    invoiceDueDate: 'Due date',
    invoicePaymentMethod: 'Payment method',
    invoicePaymentMethods: 'Payment methods',
    invoiceOfferAllMethods: 'Offer all active payment methods',
    invoiceNoPaymentMethods: 'No payment methods configured yet.',
    saveInvoice: 'Save',
    invoiceEdit: 'Edit',
    invoiceEditDone: 'Done',
    invoiceSend: 'Send',
    invoiceCreated: 'Invoice created',
    invoiceUpdated: 'Invoice updated',
    invoiceSentMsg: 'Invoice sent',
    invoiceConfirmPayment: 'Confirm payment',
    invoiceConfirmedMsg: 'Payment confirmed',
    invoiceLoadError: 'Could not load this invoice.',
    noInvoicesFound: 'No invoices yet',
    noInvoicesDescription: 'Create an invoice from a job.',
    invoicesOwnerOnly: 'Only owners can view all invoices.',
    invoiceCreatedAt: 'Created',
    invoiceSentAt: 'Sent',
    invoicePaidAt: 'Paid',
    invoiceStatusUnpaid: 'Unpaid',
    invoiceStatusPendingConfirmation: 'Pending confirmation',
    invoiceStatusPaid: 'Paid',
    invoicePhaseDraft: 'Draft',
    invoicePhaseSent: 'Sent',

    // Invoice share links
    invoiceShareLink: 'Share link',
    invoiceShareLinkReady: 'Share link ready',
    invoiceShareLinkPanelTitle: 'Send this invoice',
    invoiceShareLinkHint:
      'Anyone with this link can view the invoice — no login needed.',
    invoiceLinkCopied: 'Link copied',
    invoiceLinkCopyFailed: 'Could not copy the link',
    invoiceShareCopy: 'Copy link',
    invoiceShareNative: 'Share',
    invoiceShareEmail: 'Email',
    invoiceShareText: 'Text',
    invoiceShareExpires: 'Expires',
    invoiceShareEmailSubject: 'Your invoice',
    invoiceShareEmailBody: 'Here is your invoice. You can view it at the link below:',
    invoiceShareClose: 'Close',

    // Public shared-invoice page (/i/:token)
    sharedInvoiceFor: 'Invoice for',
    sharedInvoiceSent: 'Sent',
    sharedInvoiceExpires: 'Expires',
    sharedInvoiceInvalid:
      'This invoice link is invalid or has expired.',
    sharedInvoiceRateLimited: 'Too many requests — try again in a moment.',
    sharedInvoiceError: 'Something went wrong loading this invoice.',
    sharedInvoiceRetry: 'Try again',
    sharedInvoiceSelectMethod: 'Select how you paid',
    sharedInvoiceClaimedByName: 'Your name (your signature)',
    sharedInvoiceOpenVenmo: 'Open in Venmo',
    sharedInvoicePayByCard: 'Pay by card',
    sharedInvoiceIvePaid: "I've paid",
    sharedInvoicePaidMsg: "Thanks! We'll confirm your payment shortly.",
    sharedInvoiceAlreadyClaimed: 'This invoice has already been marked paid.',
    sharedInvoiceClaimError: 'Could not save your payment. Please try again.',
    sharedInvoiceCheckoutError: 'Could not start checkout. Please try again.',
    sharedInvoicePendingConfirmation:
      'Payment received — waiting on the business to confirm it.',
    sharedInvoicePaid: 'This invoice has been paid.',

    // Business logo (/business-page)
    businessLogoTitle: 'Business logo',
    businessLogoSubtitle: 'Shown on your invoices and quotes.',
    businessLogoNone: 'No logo uploaded yet.',
    businessLogoAlt: 'Business logo',
    sharedDocumentLogoAlt: 'Business logo',
    businessLogoUpload: 'Upload logo',
    businessLogoUploading: 'Uploading…',
    businessLogoUpdated: 'Logo updated',

    // Calendar feed sync (/business-page)
    calendarSyncTitle: 'Calendar sync',
    calendarSyncSubtitleOwner:
      'Subscribe your calendar app to every visit in your business.',
    calendarSyncSubtitleEmployee:
      "Subscribe your calendar app to the visits you're assigned to.",
    calendarSyncLocked: 'Upgrade to the Team or Pro plan to sync your calendar.',
    calendarStatusError: 'Could not load your calendar sync status.',
    calendarGenerate: 'Generate link',
    calendarRegenerate: 'Regenerate',
    calendarRevoke: 'Revoke',
    calendarConnectedSince: 'Connected since {date}.',
    calendarShownOnceHint:
      'The link is shown once, right after you generate it. Regenerate to see it again.',
    calendarFeedReady: 'Calendar link ready',
    calendarFeedRevoked: 'Calendar sync revoked',
    calendarLinkCopied: 'Link copied',
    calendarLinkCopyFailed: 'Could not copy the link',
    calendarCopy: 'Copy link',
    calendarAddGoogle: 'Add to Google Calendar',
    calendarAddApple: 'Add to Apple Calendar',
    calendarAddOutlook: 'Add to Outlook',
    calendarEmployeeLabel: 'Feed scope',
    calendarEmployeeAll: 'All visits',
    calendarWarnPassword:
      'Treat this link like a password — anyone with it can see this schedule.',
    calendarWarnNotRealtime:
      'Calendar apps refresh subscriptions every few hours, so changes are not instant.',
    calendarWarnOneWay:
      'Sync is one-way — editing an event in your calendar does nothing in VizitPro.',
    calendarRegenerateConfirm:
      'This replaces your current link. Calendars using the old link stop updating until you re-add the new one.',
    calendarRevokeConfirm:
      "Calendars you've already added will stop receiving updates.",
    calendarCancel: 'Cancel',
    calendarConfirm: 'Confirm',
    calendarSubscribe: 'Subscribe',
    calendarFeedName: 'VizitPro — {business}',

    // Billing (/billing)
    billingNav: 'Billing',
    billingTitle: 'Subscription & Billing',
    billingSubtitle: 'Manage your plan, payment method, and billing history.',
    billingCurrentPlan: 'Current plan',
    billingManagePlan: 'Manage billing',
    billingManagePlanLocked: 'Complete checkout below before you can manage billing.',
    billingSubscribe: 'Subscribe',
    billingChangePlan: 'Change plan',
    billingCycleMonthly: 'Monthly',
    billingCycleAnnual: 'Annual',
    billingPerMonth: '/mo',
    billingPerYear: '/yr',
    billingStatusTrialing: 'Trial',
    billingStatusActive: 'Active',
    billingStatusPastDue: 'Past due',
    billingStatusCanceled: 'Canceled',
    billingTrialBannerText: 'Your trial ends {when} — subscribe to keep access.',
    billingTrialEndedText: 'Your trial has ended. Subscribe to keep access.',
    billingPastDueNotice:
      "We couldn't process your last payment. Update your payment method to avoid losing access.",
    billingCanceledNotice: 'Your subscription is canceled. Subscribe again to regain access.',
    billingCheckoutError: 'Could not start checkout. Please try again.',
    billingPortalError: 'Could not open the billing portal. Please try again.',
    billingSuccessTitle: "You're subscribed!",
    billingSuccessSubtitle:
      "Thanks for subscribing. It can take a moment for your account to update — you're all set either way.",
    billingSuccessBackLink: 'Back to billing',
    billingCancelTitle: 'No changes made',
    billingCancelSubtitle: 'You backed out of checkout — your plan is unchanged.',
    billingCancelBackLink: 'Try again',
  },

  es: {
    // Common
    next: 'Siguiente',
    back: 'Atrás',
    complete: 'Completar configuración',
    loading: 'Cargando…',
    selectOption: 'Selecciona una opción',
    required: 'Este campo es obligatorio',

    // Language step
    onboardingTitle: 'Bienvenido a VizitPro',
    onboardingSubtitle: 'Configuremos tu negocio en unos pocos pasos.',
    preferredLanguage: 'Idioma preferido',
    english: 'Inglés',
    spanish: 'Español',
    businessName: 'Nombre del negocio',
    businessNamePlaceholder: 'ej. Limpieza Amanecer',
    businessType: 'Tipo de negocio',
    businessTypes: {
      cleaning: 'Servicios de limpieza',
      landscaping: 'Servicios de jardinería',
    },

    // Profile step
    profileTitle: 'Tu información',
    profileSubtitle: 'Cuéntanos sobre la persona que administra este negocio.',
    fullName: 'Nombre completo',
    fullNamePlaceholder: 'Juan Pérez',
    address: 'Dirección del negocio',
    addressPlaceholder: '123 Calle Principal, Ciudad, Estado, CP',
    phone: 'Número de teléfono',
    phonePlaceholder: '(555) 000-0000',

    // Tier step
    tierTitle: 'Elige tu plan',
    tierSubtitle: 'Elige el plan que se adapte a tu equipo. Puedes cambiarlo en cualquier momento.',
    perMonth: '/mes',
    users: 'Usuarios',
    customers: 'Clientes',
    unlimited: 'Ilimitados',
    upTo: 'Hasta',
    features: 'Funciones',
    scheduling: 'Programación',
    invoices: 'Facturas',
    quotes: 'Cotizaciones',
    messaging: 'Mensajería',
    businessPage: 'Página de negocio',
    payments: 'Pagos',
    analytics: 'Análisis',
    paymentFees: 'Comisiones de pago',
    selectPlan: 'Seleccionar plan',
    recommended: 'Recomendado',
    tiers: {
      solo: {
        name: 'Solo',
        price: '$34.99',
        users: '1',
        businessPage: 'Básica',
        paymentFees: 'Traspaso',
        analytics: 'Básico',
      },
      team: {
        name: 'Equipo',
        price: '$49',
        users: 'Hasta 5',
        businessPage: 'Completa',
        paymentFees: '2.2% + $0.30',
        analytics: 'Estándar',
      },
      pro: {
        name: 'Pro',
        price: '$99',
        users: 'Hasta 20',
        businessPage: 'Completa + Dominio propio',
        paymentFees: 'Gratis',
        analytics: 'Avanzado',
      },
    },

    // Navigation
    jobs: 'Trabajos',
    quotesNav: 'Cotizaciones',
    invoicesNav: 'Facturas',
    customersNav: 'Clientes',
    businessPageNav: 'Página de negocio',
    employees: 'Empleados',
    paymentsNav: 'Pagos',
    signOut: 'Cerrar sesión',

    // Dashboard placeholder
    comingSoon: 'Próximamente',
    pageUnderConstruction: 'Esta sección está en construcción.',

    // Step labels
    stepBusiness: 'Negocio',
    stepProfile: 'Perfil',
    stepPlan: 'Plan',

    // Payments page
    paymentMethodsTitle: 'Métodos de pago',
    paymentMethodsSubtitle: 'Configura cómo tus clientes pueden pagarte.',
    addPaymentMethod: 'Agregar método de pago',
    configured: 'Configurado',
    notConfigured: 'Sin configurar',
    configure: 'Configurar',
    editConfig: 'Editar',
    removeConfig: 'Desactivar',
    saveConfig: 'Guardar',
    cancel: 'Cancelar',
    chooseMethodToAdd: 'Elige un método de pago para agregar',
    paymentMethodLabelField: 'Etiqueta',
    paymentMethodLabelPlaceholder: 'ej. Cuenta principal',
    paymentMethodActive: 'Activo',
    paymentMethodInactive: 'Inactivo',
    paymentMethodCreated: 'Método de pago agregado',
    paymentMethodUpdated: 'Método de pago actualizado',

    // Zelle
    zelleTitle: 'Zelle',
    zelleDescription: 'Acepta pagos por Zelle. Comparte tu número o correo.',
    zellePhone: 'Teléfono o correo electrónico',
    zellePhonePlaceholder: 'ej. (555) 000-0000 o tu@correo.com',

    // Stripe
    stripeTitle: 'Stripe',
    stripeDescription: 'Acepta tarjetas de crédito/débito de forma segura con Stripe.',
    stripeConnectTitle: 'Aceptar pagos con tarjeta',
    stripeConnectDescription: 'Conéctate con Stripe para que los clientes paguen facturas con tarjeta.',
    stripeConnectConnected: 'Conectado',
    stripeConnectNotConnected: 'No conectado',
    stripeConnectButton: 'Conectar con Stripe',
    stripeConnectPending: 'Incorporación iniciada — complétala en Stripe para empezar a aceptar tarjetas.',
    stripeConnectError: 'No se pudo iniciar la incorporación con Stripe.',

    // Venmo
    venmoTitle: 'Venmo',
    venmoDescription: 'Acepta pagos por Venmo.',
    venmoUsername: 'Usuario de Venmo',
    venmoUsernamePlaceholder: '@TuNegocio',

    // Cash & check
    cashTitle: 'Efectivo',
    cashDescription: 'Acepta pagos en efectivo en persona.',
    checkTitle: 'Cheque',
    checkDescription: 'Acepta cheques.',

    // Jobs page
    todaysJobs: 'Trabajos de hoy',
    allJobs: 'Todos los trabajos',
    filterByCustomer: 'Filtrar por cliente',
    filterByDate: 'Fecha',
    filterByType: 'Tipo',
    allTypes: 'Todos los tipos',
    recurring: 'Recurrente',
    oneTime: 'Una vez',
    allDates: 'Todas las fechas',
    today: 'Hoy',
    thisWeek: 'Esta semana',
    thisMonth: 'Este mes',
    jobType: 'Tipo',
    jobStatus: 'Estado',
    scheduled: 'Programado',
    inProgress: 'En progreso',
    completed: 'Completado',
    cancelled: 'Cancelado',
    customer: 'Cliente',
    assignedTo: 'Asignado a',
    jobAddress: 'Dirección',
    duration: 'Duración',
    noJobsFound: 'No se encontraron trabajos',
    noJobsDescription: 'Intenta ajustar los filtros o agrega un nuevo trabajo.',
    addJob: 'Agregar trabajo',
    previous: 'Anterior',
    nextPage: 'Siguiente',
    pageOf: 'de',
    showingJobs: 'Mostrando',
    jobsCount: 'trabajos',
    clearFilters: 'Limpiar filtros',
    searchCustomer: 'Buscar cliente…',

    // Employee schedule
    schedule: 'Agenda',
    scheduleVisitsCount: 'visitas',
    scheduleEmpty: 'No hay visitas programadas',
    employeeScheduleBack: 'Volver a la agenda',

    // Calendar view
    calendarView: 'Calendario',
    listView: 'Lista',
    tableView: 'Tabla',
    calendarPrevMonth: 'Mes anterior',
    calendarNextMonth: 'Mes siguiente',
    calendarMore: '+{n} más',
    calendarNoJobsMonth: 'No hay trabajos programados este mes',
    calendarNoVisitsMonth: 'No hay visitas programadas este mes',
    calendarViewMonth: 'Mes',
    calendarViewAgenda: 'Agenda',
    calendarAgendaDate: 'Fecha',
    calendarAgendaTime: 'Hora',
    calendarAgendaEvent: 'Evento',
    calendarAllDay: 'Todo el día',
    calendarNoEventsRange: 'Nada programado en este rango',
    jobRescheduled: 'Trabajo reprogramado',
    visitRescheduled: 'Visita reprogramada',
    reschedule: 'Reprogramar',
    rescheduleCurrentLabel: 'Programado actualmente',
    rescheduleNewLabel: 'Nueva fecha',

    // Add job modal
    addJobTitle: 'Agregar nuevo trabajo',
    copyJobTitle: 'Copiar trabajo',
    jobCopyAction: 'Copiar',
    jobCopyJob: 'Copiar trabajo',
    jobTitle: 'Título del trabajo',
    jobTitlePlaceholder: 'ej. Limpieza semanal del hogar',
    jobCustomer: 'Cliente',
    jobCustomerPlaceholder: 'Seleccionar cliente',
    jobCustomerNew: 'Nuevo cliente…',
    jobNoEligibleCustomers: 'Aún no hay clientes con una cuenta.',
    jobManageCustomers: 'Administrar clientes',
    jobCustomerNoAccount:
      'Ese cliente todavía no tiene una cuenta vinculada, por lo que no se puede crear un trabajo para él.',
    jobAssignedTo: 'Asignado a',
    jobAssignedToPlaceholder: 'Seleccionar empleado',
    jobDate: 'Fecha',
    jobStartDate: 'Fecha de inicio',
    jobEndDate: 'Fecha de fin',
    jobEndDateModeSame: 'Mismo día',
    jobEndDateModeCustom: 'Personalizado',
    jobEndDateModeYear: 'Contrato anual',
    jobStartTime: 'Hora de inicio',
    jobEndTime: 'Hora de fin',
    jobSelectTimePlaceholder: 'Seleccionar hora',
    jobTime: 'Hora',
    jobDuration: 'Duración',
    jobDurationPlaceholder: 'ej. 2 hrs',
    jobDescription: 'Descripción',
    jobDescriptionPlaceholder: 'Qué hay que hacer…',
    jobTotalAmount: 'Monto total',
    jobTotalAmountPlaceholder: '0.00',
    jobNotes: 'Notas (opcional)',
    jobNotesPlaceholder: 'Instrucciones especiales…',
    jobTypeLabel: 'Tipo de trabajo',
    saveJob: 'Guardar trabajo',
    jobAdded: 'Trabajo agregado',
    newCustomerName: 'Nombre del nuevo cliente',
    newCustomerNamePlaceholder: 'Nombre completo o nombre del negocio',

    // Job recurrence
    jobRepeats: 'Se repite',
    jobFrequency: 'Frecuencia',
    frequencyWeekly: 'Semanal',
    frequencyMonthly: 'Mensual',
    frequencyCustom: 'Personalizada',
    jobRepeatEvery: 'Repetir cada',
    jobRepeatEveryWeeks: 'semana(s)',
    jobRepeatEveryMonths: 'mes(es)',
    jobDaysOfWeek: 'Los días',
    jobDayOfMonth: 'Día del mes',
    jobCustomRule: 'Regla personalizada (RRULE)',
    jobCustomRulePlaceholder: 'FREQ=DAILY;INTERVAL=2',
    jobSeriesEndDate: 'Termina el',
    weekdayShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],

    // Job team / employee assignment
    jobTeam: 'Equipo asignado',
    jobAssignEmployee: 'Asignar empleado',
    jobSelectEmployee: 'Selecciona un empleado',
    jobPayAmount: 'Pago',
    jobPayAmountPlaceholder: '0.00',
    jobAssignAdd: 'Asignar',
    jobUnassign: 'Quitar',
    jobUnassignConfirm: '¿Confirmar?',
    jobNoAssignedEmployees: 'Nadie asignado todavía.',
    jobAllEmployeesAssigned: 'Todos los empleados activos están asignados.',
    jobEmployeeAssigned: 'Empleado asignado',
    jobEmployeeRemoved: 'Empleado eliminado',
    jobEmployeePayUpdated: 'Pago actualizado',
    jobBadgeUnassigned: 'Sin asignar',
    jobBadgeAssigned: 'asignado(s)',
    jobBadgeVisits: 'visitas',
    assignmentAll: 'Todos',
    assignmentUnassigned: 'Sin asignar',
    assignmentAssigned: 'Asignado',

    // Job detail modal
    jobUpdated: 'Trabajo actualizado',
    editJob: 'Editar trabajo',
    paymentStatusLabel: 'Estado de pago',
    paymentUnpaid: 'Sin pagar',
    paymentPaid: 'Pagado',

    // Recurrence preview (add-job form)
    recurrencePreviewCreatesOne: 'Crea 1 visita:',
    recurrencePreviewCreates: 'Crea {n} visitas:',
    recurrencePreviewFirstN: 'se muestran las primeras {n}',
    recurrencePreviewPickDays: 'Elige al menos un día de la semana para ver las fechas.',
    recurrencePreviewCustom: 'La vista previa no está disponible para reglas personalizadas.',

    // Recurring job series
    seriesTitle: 'Serie recurrente',
    seriesBack: 'Volver a trabajos',
    seriesOwnerOnly: 'Solo los propietarios pueden ver una serie.',
    seriesUpsellTitle: 'Los trabajos recurrentes son una función de pago',
    seriesUpsellBody: 'Actualiza al plan Team o Pro para programar visitas recurrentes.',
    seriesViewSeries: 'Ver serie',
    seriesLoadError: 'No se pudo cargar esta serie.',
    seriesPipelineSummary:
      '{total} visitas · {completed} hechas · {cancelled} canceladas',
    seriesPipelineNext: 'próxima {date}',
    seriesPipelineNoNext: 'sin visitas próximas',
    seriesLaborPerVisit: 'Mano de obra / visita',
    seriesExpectedPerVisit: 'Esperado / visita',
    seriesProfitPerVisit: 'Ganancia / visita',
    seriesVisitsCompleted: 'Visitas',
    seriesLaborVisits: '{n} visitas',
    seriesLastServiced: 'Último servicio {rel}',
    seriesNeverServiced: 'Aún no hay visitas completadas',
    seriesNextVisit: 'Próxima visita {rel}',

    // Series crew bar
    seriesCrew: 'Equipo',
    seriesAddCrew: 'Agregar equipo a la serie',
    seriesCrewEmployee: 'Empleado',
    seriesCrewSelectEmployee: 'Selecciona un empleado',
    seriesScopeLabel: 'Aplica a',
    seriesScopeAll: 'Todas las visitas de la serie',
    seriesScopeUpcoming: 'Solo visitas próximas',
    seriesScopeUnstarted: 'Visitas aún no iniciadas',
    seriesChangePay: 'Cambiar pago en la serie',
    seriesApplyPay: 'Aplicar',
    seriesRemoveFromSeries: 'Quitar de la serie',
    seriesRemoveConfirm: 'Confirmar',
    seriesRemoveHint:
      'Se quita de las visitas próximas; las completadas conservan su registro de pago.',
    seriesCrewNoAssignments: 'Aún no hay equipo asignado en esta serie.',
    seriesCrewNoMatch: 'Ninguna visita coincidió con ese alcance.',
    seriesCrewAlreadyAll: 'Ya está asignado a todas las visitas que coinciden.',
    seriesPayNoChange: 'No se necesitan cambios de pago.',
    seriesCrewAddedTo: 'Se agregó a {name} en {n} visitas.',
    seriesPayUpdatedOn: 'Se actualizó el pago en {n} visitas.',
    seriesCrewRemovedFrom: 'Se quitó a {name} de {n} visitas.',
    seriesCrewCancel: 'Cancelar',

    // Occurrence table + filters
    seriesColDate: 'Fecha',
    seriesColStatus: 'Estado',
    seriesColPayment: 'Pago',
    seriesColCrew: 'Equipo',
    seriesColBilled: 'Facturado',
    seriesColCollected: 'Cobrado',
    seriesColActions: 'Acciones',
    seriesNoOccurrences: 'Ninguna visita coincide con estos filtros.',
    seriesMarkPaid: 'Marcar pagada',
    seriesSendInvoice: 'Enviar factura',
    seriesViewInvoice: 'Ver factura',
    seriesSkipVisit: 'Omitir esta visita',
    seriesSkipConfirm: 'Confirmar',
    seriesAdvanceToStarted: 'Iniciar',
    seriesAdvanceToCompleted: 'Completar',
    seriesFilterPayment: 'Pago',
    seriesFilterPaymentAll: 'Todos los pagos',
    seriesFilterPaymentPaid: 'Pagadas',
    seriesFilterPaymentUnpaid: 'Sin pagar',
    seriesFilterPaymentPartial: 'Parcial',
    seriesFilterDate: 'Fecha',
    seriesFilterDateAll: 'Todas las fechas',
    seriesFilterDatePast: 'Pasadas',
    seriesFilterDateUpcoming: 'Próximas',
    paymentPartial: 'Parcial',
    seriesPartialShort: 'Parcial — faltan {amount}',

    // Record payment form
    jobRecordPayment: 'Registrar pago',
    jobAmountCollected: 'Monto cobrado',
    jobPaymentMethod: 'Método de pago',
    jobDefaultPaymentMethod: 'Método de pago predeterminado',
    jobDefaultPaymentMethodNone: 'Sin predeterminado',
    paymentMethodZelle: 'Zelle',
    paymentMethodVenmo: 'Venmo',
    paymentMethodStripe: 'Stripe',
    paymentMethodCash: 'Efectivo',
    paymentMethodCheck: 'Cheque',
    paymentMethodOther: 'Otro',
    jobPaymentRecorded: 'Pago registrado',
    jobPaidOn: 'Pagado {date}',
    jobCompletedOn: 'Completado {date}',
    jobPaymentSave: 'Guardar pago',

    // Job notes
    jobNotesHeading: 'Notas',
    jobNoteBody: 'Nota',
    jobNotePlaceholder: 'Agrega una nota sobre esta visita…',
    jobAddNote: 'Agregar nota',
    jobNoteAdded: 'Nota agregada',
    jobNoNotes: 'Aún no hay notas.',

    // Job attachments
    jobAttachmentsHeading: 'Fotos y archivos',
    jobAddAttachment: 'Subir',
    jobAttachmentCaption: 'Descripción (opcional)',
    jobAttachmentCaptionPlaceholder: 'ej. Antes — jardín delantero',
    jobAttachmentAdded: 'Archivo agregado',
    jobNoAttachments: 'Aún no hay fotos ni archivos.',
    jobAttachmentUploading: 'Subiendo…',
    jobAttachmentOpen: 'Abrir archivo',

    // Employees page
    addEmployee: 'Agregar empleado',
    addEmployeeTitle: 'Agregar empleado',
    allEmployees: 'Todos los empleados',
    activeEmployees: 'Activos',
    inactiveEmployees: 'Inactivos',
    employeeFullName: 'Nombre completo',
    employeeFullNamePlaceholder: 'Juana Pérez',
    employeePhone: 'Teléfono',
    employeeEmail: 'Correo',
    employeeEmailPlaceholder: 'juana@ejemplo.com',
    employeeTitle: 'Puesto',
    employeeTitlePlaceholder: 'p. ej. Limpiador líder',
    employeeHiredAt: 'Fecha de contratación',
    employeeStatus: 'Estado',
    employeeActive: 'Activo',
    employeeInactive: 'Inactivo',
    employeeNoTitle: 'Sin puesto',
    employeeLoadError: 'No se pudo cargar este empleado.',
    employeeAdded: 'Empleado agregado',
    employeeUpdated: 'Empleado actualizado',
    employeeInviteLink: 'Enviar enlace de invitación',
    employeeInviteLinkReady: 'Enlace de invitación listo',
    employeeInviteLinkPanelTitle: 'Enlace de invitación del empleado',
    employeeInviteLinkHint:
      'Comparte este enlace para que el empleado inicie sesión y configure su cuenta.',
    employeeInviteLinkCopied: 'Enlace copiado',
    employeeInviteLinkCopyFailed: 'No se pudo copiar el enlace',
    employeeInviteCopy: 'Copiar enlace',
    employeeInviteNative: 'Compartir',
    employeeInviteEmail: 'Correo',
    employeeInviteClose: 'Cerrar',
    employeeInviteEmailSubject: 'Configura tu cuenta',
    employeeInviteEmailBody:
      'Usa el siguiente enlace para iniciar sesión y configurar tu cuenta:',
    employeeInviteExpiryNote: 'Este enlace es confidencial — no lo compartas públicamente.',
    employeeNoEmailTooltip: 'Agrega un correo electrónico antes de enviar un enlace de invitación.',
    saveEmployee: 'Guardar empleado',
    noEmployeesFound: 'No se encontraron empleados',
    noEmployeesDescription: 'Intenta cambiar el filtro o agrega un nuevo empleado.',
    filterByStatus: 'Estado',
    allStatuses: 'Todos',

    // Payroll page
    payrollNav: 'Nómina',
    payrollTitle: 'Nómina',
    payrollEmpty: 'No se debe nada a nadie en este período.',
    payrollFilterFrom: 'Desde',
    payrollFilterTo: 'Hasta',
    payrollVisitsOwed: 'Visitas pendientes',
    payrollTotalOwed: 'Total adeudado',
    payrollViewHistory: 'Historial de pagos',
    payrollViewOwed: 'Pendiente',
    payrollHistoryEmpty: 'Aún no hay pagos registrados.',
    payrollBackToList: 'Volver a nómina',
    payrollSelectAll: 'Seleccionar todo',
    payrollDeselectAll: 'Deseleccionar todo',
    payrollSelectedTotal: '{count} visitas seleccionadas · {amount}',
    payrollNotePlaceholder: 'Nota (opcional), p. ej. Zelle, cheque #123…',
    payrollMarkAsPaid: 'Marcar como pagado',
    payrollPaymentRecorded: 'Pago registrado',
    payrollPaymentDetailTitle: 'Detalle del pago',
    payrollNoOwedVisits: 'Este empleado no tiene nada pendiente en este período.',
    payrollPaidAt: 'Pagado',
    payrollLoadError: 'No se pudo cargar la información de nómina.',
    payrollLineItem: 'Visita',

    // Customers page
    addCustomer: 'Agregar cliente',
    addCustomerTitle: 'Agregar cliente',
    searchCustomers: 'Buscar clientes…',
    customerFullName: 'Nombre completo',
    customerFullNamePlaceholder: 'Juana Pérez',
    customerEmail: 'Correo',
    customerEmailPlaceholder: 'juana@ejemplo.com',
    customerServiceAddress: 'Dirección de servicio',
    customerServiceAddressPlaceholder: '1847 Maple Ave, Austin, TX 78701',
    customerNotes: 'Notas',
    customerNotesPlaceholder: 'Notas sobre este cliente…',
    customerStatus: 'Estado',
    saveCustomer: 'Guardar cliente',
    customerAdded: 'Cliente agregado',
    customerUpdated: 'Cliente actualizado',
    customerLoadError: 'No se pudo cargar este cliente.',
    noCustomersFound: 'No se encontraron clientes',
    noCustomersDescription: 'Intenta otra búsqueda o agrega un nuevo cliente.',
    customerActive: 'Activo',
    customerInactive: 'Inactivo',

    // Quotes page
    quotesTitle: 'Cotizaciones',
    quotesCount: 'cotizaciones',
    addQuote: 'Agregar cotización',
    addQuoteTitle: 'Nueva cotización',
    quoteJob: 'Trabajo',
    quoteJobPlaceholder: 'Selecciona un trabajo',
    quoteNoJobs: 'Crea un trabajo primero y luego cotízalo.',
    quoteAmount: 'Monto',
    quoteAmountPlaceholder: '0.00',
    quoteAmountInvalid: 'Ingresa un monto válido',
    quoteStatus: 'Estado',
    quoteStatusPending: 'Pendiente',
    quoteStatusAccepted: 'Aceptada',
    quoteStatusDeclined: 'Rechazada',
    quotePhaseDraft: 'Borrador',
    quotePhaseSent: 'Enviada',
    quotePhaseApproved: 'Aprobada',
    quoteSend: 'Enviar',
    quoteApprove: 'Aprobar',
    quoteAccept: 'Aceptar',
    quoteDecline: 'Rechazar',
    quoteDeclineConfirm: 'Confirmar rechazo',
    saveQuote: 'Guardar',
    quoteAdd: 'Agregar',
    quoteCreated: 'Cotización creada',
    quoteUpdated: 'Cotización actualizada',
    quoteSentMsg: 'Cotización enviada',
    quoteApprovedMsg: 'Cotización aprobada',
    quoteRespondedMsg: 'Respuesta guardada',
    quoteLoadError: 'No se pudo cargar esta cotización.',
    noQuotesFound: 'Aún no hay cotizaciones',
    noQuotesDescription: 'Crea una cotización desde un trabajo.',
    quotesOwnerOnly: 'Solo los propietarios pueden ver todas las cotizaciones.',
    quoteCreatedAt: 'Creada',
    quoteSentAt: 'Enviada',
    quoteApprovedAt: 'Aprobada',
    quoteRespondedAt: 'Respondida',
    quoteLineItems: 'Conceptos',
    quoteLineItemDescription: 'Descripción',
    quoteLineItemDescriptionPlaceholder: 'p. ej. Limpieza profunda, 3 recámaras',
    quoteLineItemAmount: 'Monto',
    quoteAddLineItem: 'Agregar concepto',
    quoteRemoveLineItem: 'Quitar concepto',
    quoteNoLineItems: 'Sin conceptos',
    quoteLineItemsSubtotal: 'Subtotal',
    quoteNotes: 'Notas',
    quoteNotesPlaceholder: 'Alcance del trabajo, términos, exclusiones…',
    quoteSetTotalManually: 'Definir total manualmente',
    quoteLineItemsReconcile: 'Conceptos',
    quoteTotal: 'Total',
    quoteEdit: 'Editar',
    quoteEditDone: 'Listo',

    // Quote share links
    quoteShareLink: 'Enlace para compartir',
    quoteShareLinkReady: 'Enlace listo',
    quoteShareLinkPanelTitle: 'Envía esta cotización',
    quoteShareLinkHint:
      'Cualquier persona con este enlace puede ver la cotización, sin iniciar sesión.',
    quoteLinkCopied: 'Enlace copiado',
    quoteLinkCopyFailed: 'No se pudo copiar el enlace',
    quoteShareCopy: 'Copiar enlace',
    quoteShareNative: 'Compartir',
    quoteShareEmail: 'Correo',
    quoteShareText: 'Mensaje',
    quoteShareExpires: 'Vence',
    quoteShareEmailSubject: 'Tu cotización',
    quoteShareEmailBody:
      'Aquí está tu cotización. Puedes verla en el siguiente enlace:',
    quoteShareClose: 'Cerrar',

    // Public shared-quote page (/q/:token)
    sharedQuoteFor: 'Cotización para',
    sharedQuoteSent: 'Enviada',
    sharedQuoteExpires: 'Vence',
    sharedQuoteInvalid:
      'Este enlace ya no es válido. Puede haber vencido o haber sido reemplazado; solicita uno nuevo al negocio.',
    sharedQuoteRateLimited: 'Demasiadas solicitudes: inténtalo de nuevo en un momento.',
    sharedQuoteError: 'Ocurrió un error al cargar esta cotización.',
    sharedQuoteRetry: 'Intentar de nuevo',
    sharedQuoteResponderName: 'Tu nombre (opcional)',
    sharedQuoteAccept: 'Aceptar cotización',
    sharedQuoteDecline: 'Rechazar',
    sharedQuoteDeclineConfirm: 'Toca de nuevo para confirmar',
    sharedQuoteAcceptedMsg: 'Aceptaste esta cotización.',
    sharedQuoteDeclinedMsg: 'Rechazaste esta cotización.',
    sharedQuoteAlreadyResponded: 'Esta cotización ya fue respondida.',
    sharedQuoteRespondError: 'No se pudo guardar tu respuesta. Inténtalo de nuevo.',

    // Invoices page
    invoicesTitle: 'Facturas',
    invoicesCount: 'facturas',
    addInvoice: 'Agregar factura',
    addInvoiceTitle: 'Nueva factura',
    invoiceJob: 'Trabajo',
    invoiceJobPlaceholder: 'Selecciona un trabajo',
    invoiceNoJobs: 'Crea un trabajo primero y luego factúralo.',
    invoiceSubtotal: 'Subtotal',
    invoiceTax: 'Impuesto',
    invoiceDiscount: 'Descuento',
    invoiceTotal: 'Total',
    invoiceShowTaxBreakdown: 'Mostrar desglose de impuestos',
    invoiceDueDate: 'Fecha de vencimiento',
    invoicePaymentMethod: 'Método de pago',
    invoicePaymentMethods: 'Métodos de pago',
    invoiceOfferAllMethods: 'Ofrecer todos los métodos de pago activos',
    invoiceNoPaymentMethods: 'Aún no hay métodos de pago configurados.',
    saveInvoice: 'Guardar',
    invoiceEdit: 'Editar',
    invoiceEditDone: 'Listo',
    invoiceSend: 'Enviar',
    invoiceCreated: 'Factura creada',
    invoiceUpdated: 'Factura actualizada',
    invoiceSentMsg: 'Factura enviada',
    invoiceConfirmPayment: 'Confirmar pago',
    invoiceConfirmedMsg: 'Pago confirmado',
    invoiceLoadError: 'No se pudo cargar esta factura.',
    noInvoicesFound: 'Aún no hay facturas',
    noInvoicesDescription: 'Crea una factura desde un trabajo.',
    invoicesOwnerOnly: 'Solo los propietarios pueden ver todas las facturas.',
    invoiceCreatedAt: 'Creada',
    invoiceSentAt: 'Enviada',
    invoicePaidAt: 'Pagada',
    invoiceStatusUnpaid: 'Sin pagar',
    invoiceStatusPendingConfirmation: 'Pendiente de confirmación',
    invoiceStatusPaid: 'Pagada',
    invoicePhaseDraft: 'Borrador',
    invoicePhaseSent: 'Enviada',

    // Invoice share links
    invoiceShareLink: 'Enlace para compartir',
    invoiceShareLinkReady: 'Enlace listo',
    invoiceShareLinkPanelTitle: 'Envía esta factura',
    invoiceShareLinkHint:
      'Cualquier persona con este enlace puede ver la factura, sin iniciar sesión.',
    invoiceLinkCopied: 'Enlace copiado',
    invoiceLinkCopyFailed: 'No se pudo copiar el enlace',
    invoiceShareCopy: 'Copiar enlace',
    invoiceShareNative: 'Compartir',
    invoiceShareEmail: 'Correo',
    invoiceShareText: 'Mensaje',
    invoiceShareExpires: 'Vence',
    invoiceShareEmailSubject: 'Tu factura',
    invoiceShareEmailBody: 'Aquí está tu factura. Puedes verla en el siguiente enlace:',
    invoiceShareClose: 'Cerrar',

    // Public shared-invoice page (/i/:token)
    sharedInvoiceFor: 'Factura para',
    sharedInvoiceSent: 'Enviada',
    sharedInvoiceExpires: 'Vence',
    sharedInvoiceInvalid:
      'Este enlace de factura no es válido o ha vencido.',
    sharedInvoiceRateLimited: 'Demasiadas solicitudes — intenta de nuevo en un momento.',
    sharedInvoiceError: 'Algo salió mal al cargar esta factura.',
    sharedInvoiceRetry: 'Intentar de nuevo',
    sharedInvoiceSelectMethod: 'Selecciona cómo pagaste',
    sharedInvoiceClaimedByName: 'Tu nombre (tu firma)',
    sharedInvoiceOpenVenmo: 'Abrir en Venmo',
    sharedInvoicePayByCard: 'Pagar con tarjeta',
    sharedInvoiceIvePaid: 'Ya pagué',
    sharedInvoicePaidMsg: '¡Gracias! Confirmaremos tu pago en breve.',
    sharedInvoiceAlreadyClaimed: 'Esta factura ya fue marcada como pagada.',
    sharedInvoiceClaimError: 'No se pudo guardar tu pago. Intenta de nuevo.',
    sharedInvoiceCheckoutError: 'No se pudo iniciar el pago. Intenta de nuevo.',
    sharedInvoicePendingConfirmation:
      'Pago recibido — esperando que el negocio lo confirme.',
    sharedInvoicePaid: 'Esta factura ha sido pagada.',

    // Business logo (/business-page)
    businessLogoTitle: 'Logo del negocio',
    businessLogoSubtitle: 'Se muestra en tus facturas y cotizaciones.',
    businessLogoNone: 'Aún no hay un logo.',
    businessLogoAlt: 'Logo del negocio',
    sharedDocumentLogoAlt: 'Logo del negocio',
    businessLogoUpload: 'Subir logo',
    businessLogoUploading: 'Subiendo…',
    businessLogoUpdated: 'Logo actualizado',

    // Calendar feed sync (/business-page)
    calendarSyncTitle: 'Sincronizar calendario',
    calendarSyncSubtitleOwner:
      'Suscribe tu app de calendario a todas las visitas de tu negocio.',
    calendarSyncSubtitleEmployee:
      'Suscribe tu app de calendario a las visitas que tienes asignadas.',
    calendarSyncLocked:
      'Mejora al plan Team o Pro para sincronizar tu calendario.',
    calendarStatusError:
      'No se pudo cargar el estado de sincronización del calendario.',
    calendarGenerate: 'Generar enlace',
    calendarRegenerate: 'Regenerar',
    calendarRevoke: 'Revocar',
    calendarConnectedSince: 'Conectado desde el {date}.',
    calendarShownOnceHint:
      'El enlace se muestra una sola vez, justo después de generarlo. Regenéralo para verlo de nuevo.',
    calendarFeedReady: 'Enlace de calendario listo',
    calendarFeedRevoked: 'Sincronización de calendario revocada',
    calendarLinkCopied: 'Enlace copiado',
    calendarLinkCopyFailed: 'No se pudo copiar el enlace',
    calendarCopy: 'Copiar enlace',
    calendarAddGoogle: 'Agregar a Google Calendar',
    calendarAddApple: 'Agregar a Apple Calendar',
    calendarAddOutlook: 'Agregar a Outlook',
    calendarEmployeeLabel: 'Alcance del feed',
    calendarEmployeeAll: 'Todas las visitas',
    calendarWarnPassword:
      'Trata este enlace como una contraseña: cualquiera que lo tenga puede ver este calendario.',
    calendarWarnNotRealtime:
      'Las apps de calendario actualizan las suscripciones cada varias horas, así que los cambios no son inmediatos.',
    calendarWarnOneWay:
      'La sincronización es unidireccional: editar un evento en tu calendario no cambia nada en VizitPro.',
    calendarRegenerateConfirm:
      'Esto reemplaza tu enlace actual. Los calendarios que usan el enlace anterior dejarán de actualizarse hasta que agregues el nuevo.',
    calendarRevokeConfirm:
      'Los calendarios que ya agregaste dejarán de recibir actualizaciones.',
    calendarCancel: 'Cancelar',
    calendarConfirm: 'Confirmar',
    calendarSubscribe: 'Suscribirse',
    calendarFeedName: 'VizitPro — {business}',

    // Billing (/billing)
    billingNav: 'Facturación',
    billingTitle: 'Suscripción y facturación',
    billingSubtitle: 'Administra tu plan, método de pago e historial de facturación.',
    billingCurrentPlan: 'Plan actual',
    billingManagePlan: 'Administrar facturación',
    billingManagePlanLocked: 'Completa el pago abajo antes de poder administrar la facturación.',
    billingSubscribe: 'Suscribirse',
    billingChangePlan: 'Cambiar plan',
    billingCycleMonthly: 'Mensual',
    billingCycleAnnual: 'Anual',
    billingPerMonth: '/mes',
    billingPerYear: '/año',
    billingStatusTrialing: 'Prueba',
    billingStatusActive: 'Activo',
    billingStatusPastDue: 'Pago vencido',
    billingStatusCanceled: 'Cancelado',
    billingTrialBannerText: 'Tu prueba termina {when} — suscríbete para conservar el acceso.',
    billingTrialEndedText: 'Tu prueba ha terminado. Suscríbete para conservar el acceso.',
    billingPastDueNotice:
      'No pudimos procesar tu último pago. Actualiza tu método de pago para no perder el acceso.',
    billingCanceledNotice: 'Tu suscripción está cancelada. Suscríbete de nuevo para recuperar el acceso.',
    billingCheckoutError: 'No se pudo iniciar el pago. Inténtalo de nuevo.',
    billingPortalError: 'No se pudo abrir el portal de facturación. Inténtalo de nuevo.',
    billingSuccessTitle: '¡Estás suscrito!',
    billingSuccessSubtitle:
      'Gracias por suscribirte. Tu cuenta puede tardar un momento en actualizarse — de cualquier forma ya quedó todo listo.',
    billingSuccessBackLink: 'Volver a facturación',
    billingCancelTitle: 'No se hicieron cambios',
    billingCancelSubtitle: 'Cancelaste el proceso de pago — tu plan no cambió.',
    billingCancelBackLink: 'Intentar de nuevo',
  },
} as const;

export type Translations = (typeof translations)[Language];
